import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormControl, FormArray, FormGroup, AbstractControl, FormBuilder } from '@angular/forms';
import { Observable, throwError, merge, EMPTY, Subject, of } from 'rxjs';
import { map, distinct, mapTo, catchError, retry, filter } from 'rxjs/operators';

import { EventType, jsonheader, ObjectType, TradeDoc } from '@app/shared/constants';

import { environment } from '@app/../environments/environment';
import { LoginService } from '@app/common/login.service';
import { LogisticService } from '@app/logistic/logistic.service';
import { Event } from '@app/shared/_models/common';
import { Sales_order, SalesOrderForm } from '@app/shared/_models/sales';

@Injectable({
  providedIn: 'root',
})
export class QuotationService {
  constructor(
    public loginService: LoginService,
    private http: HttpClient,
    private logisticService: LogisticService,
  ) {}

  public subject = new Subject<any>();

  /**
   * Action d'avoir différée après passage par l'état PREPARATION (création des créances).
   * Sur un brouillon négatif (CREATED), l'action finale (remboursement espèces ou émission avoir sans décaissement)
   * s'enchaîne automatiquement après le rechargement serveur sans redemander confirmation à l'utilisateur.
   */
  public pendingAvoirAction: 'REFUND_CASH' | 'AVOIR_ONLY' | null = null;
  // Pièce visée par pendingAvoirAction (0 = brouillon, remplacé par l'id rendu à sa création) : le service survit à l'écran et à la pièce.
  public pendingAvoirOrderId: number | null = null;

  sendAddproductpackagingfromHistory(product: any, qty: number = 1) {
    this.subject.next({ product, qty });
  }

  getQuotationAsFormGroup(id: number, options: string = ''): Observable<FormGroup<SalesOrderForm> | undefined> {
    return this.getQuotationById(id, options).pipe(
      map((orders: Sales_order[]) => {
        console.log('orders[0]', orders[0]);
        if (orders.length === 0) return undefined;
        return Sales_order.asFormGroup(orders[0]); // no order send undefined
      }),
    );
  }

  getQuotationById(id: number, options: string = ''): Observable<any> {
    if (id === 0) return of([]);
    return this.http.get<void>(environment.server_url + '/sales/order/' + id + options);
    // return this.http.get<void>(environment.server_url + "/sales/orders?" + q + (load_lines?'&load_lines=true':'') + (load_deliveries?'&load_deliveries=true':''));
  }

  getCustomerHistory(customerId: number, curentOrderId: number): Observable<any> {
    return this.http.get<void>(environment.server_url + '/sales/orders?q=customer:' + customerId + ' AND limit:8&o=lines').pipe(
      map((orders: any) => {
        return orders.filter((order: any) => order.id !== curentOrderId); // Exclude current order
      }),
    );
  }

  /**
   * Récupère la liste des factures / commandes enfants émises pour un contrat parent (LOCATION).
   * @param parentOrderId Identifiant du contrat de location parent
   * @returns Observable de la liste des commandes enfants
   */
  getChildOrders(parentOrderId: number): Observable<Sales_order[]> {
    if (!parentOrderId) return of([]);
    return this.http.get<Sales_order[]>(`${environment.server_url}/sales/orders?q=parent_order_id:${parentOrderId}`);
  }

  // Commandes de vente (F2) d'un dossier de location (data.rental_order_id), avec lignes et BL : le passage les livre avec le dossier (JP 30/10).
  getRentalSaleOrders(rentalOrderId: number): Observable<Sales_order[]> {
    if (!rentalOrderId) return of([]);
    return this.http.get<Sales_order[]>(`${environment.server_url}/sales/orders?q=rental_order_id:${rentalOrderId}&o=lines,deliveries`);
  }

  // move to common service
  // getTree(path: string, typ?:number): Observable<any> {
  // 	let typcrtiteria:string = (typ ? "?typ=" + typ : "");
  // 	return this.http.get<void>(environment.server_url + "/common/tree/desc/" + path + typcrtiteria);
  // }

  getCustomerPricelist(id: number): Observable<any> {
    return this.http.get<void>(environment.server_url + '/sales/customerprice/' + id);
  }

  postQuotationEvent(eventtyp: number, formGroup: FormGroup): Observable<any> {
    this.logisticService.beforeSubmit(formGroup); // Do some type adjustements
    let event: Event = {
      id: 0,
      typ: eventtyp, // 1: Insert, 2: Update, 3: Delete
      userid: this.loginService.loggeduser?.id,
      objectid: formGroup.get('id')?.value,
      objecttyp: ObjectType.ORDER, // Order
      data: formGroup.value,
    };
    console.log('[postQuotationEvent] Event: ', JSON.stringify(event, null, 2));
    return this.http.post<any>(environment.server_url + '/event', event, { headers: jsonheader });
  }

  get_docs_by_object_id(id: number): Observable<any> {
    return this.http.get<void>(environment.server_url + '/docsbyobjectid/' + id);
  }

  postInvoicingEvent(selectedDeliveryIds: Set<Number>): Observable<any> {
    let event: Event = {
      id: 0,
      typ: EventType.INVOICING, // Special type
      userid: this.loginService.loggeduser?.id,
      objectid: 0,
      objecttyp: ObjectType.DELIVERY, // Delivery
      data: { selectedDeliveryIds: [...selectedDeliveryIds] },
    };
    return this.http.post<any>(environment.server_url + '/event', event, { headers: jsonheader });
  }

  postHCCardEvent(eventtyp: number, data: any): Observable<any> {
    // Post HC patients (create fse_patient occ.) from hc card WARNING Duplicate in fse lib!
    console.log('In postHCCardEvent');
    let event: Event = {
      id: 0,
      typ: eventtyp,
      userid: this.loginService.loggeduser?.id,
      objectid: 0,
      objecttyp: ObjectType.HCCARD,
      data: data,
    };
    return this.http.post<any>(environment.server_url + '/event', event, { headers: jsonheader });
  }

  postHCCreatePartnerEvent(data: any): Observable<any> {
    // Post HC patient. Create c_partner from béneficiaire to assign it to sales_order. calls server side process_patient_create_partner
    console.log('In postHCCreatePartnerEvent');
    let event: Event = {
      id: 0,
      typ: EventType.FSE_CREATE_PARTNER,
      userid: this.loginService.loggeduser?.id,
      objectid: 0,
      objecttyp: ObjectType.HCPATIENT,
      data: data,
    };
    return this.http.post<any>(environment.server_url + '/event', event, { headers: jsonheader });
  }

  /**
   * Posts an event to take and seal a down payment (acompte) on a sales order.
   *
   * @param orderId Sales order identifier
   * @param amount Gross down payment amount (TTC)
   * @param paymentMethod Payment method identifier from category 7
   * @param paymentData Optional payment metadata (check number, bank, card auth)
   * @param note Optional note / commercial memo
   * @returns Observable with backend ApiResponse
   */
  postTakeDepositEvent(orderId: number, amount: number, paymentMethod: number, paymentData?: any, note?: string): Observable<any> {
    const event: Event = {
      id: 0,
      typ: EventType.TAKE_DEPOSIT,
      userid: this.loginService.loggeduser?.id || 0,
      objectid: orderId,
      objecttyp: ObjectType.ORDER,
      data: {
        order_id: orderId,
        amount: amount,
        payment_method: paymentMethod,
        ...(paymentData ? { payment_data: paymentData } : {}),
        ...(note ? { note: note } : {}),
      },
    };
    console.log('[postTakeDepositEvent] Event: ', JSON.stringify(event, null, 2));
    return this.http.post<any>(environment.server_url + '/event', event, { headers: jsonheader });
  }

  // Événement 105 : le serveur re-rend la pièce `doc` avec le trait (data-URL PNG) et la note, puis range le PDF scellé sur la commande.
  postTradeDocSignEvent(orderId: number, doc: TradeDoc, signature: string, note = ''): Observable<any> {
    const event: Event = {
      id: 0,
      typ: EventType.TRADE_DOC_SIGN,
      userid: this.loginService.loggeduser?.id || 0,
      objectid: orderId,
      objecttyp: ObjectType.ORDER,
      data: { order_id: orderId, doc, signature, signature_note: note },
    };
    return this.http.post<any>(environment.server_url + '/event', event, { headers: jsonheader });
  }

  /**
   * Substitutes a payment method on a finalized/paid sales order.
   * Calls POST /sales/order/{id}/payment/substitute.
   *
   * @param orderId Sales order identifier
   * @param originalPaymentId Original payment identifier to cancel/substitute
   * @param newMethod New payment method code from c_values 7
   * @param reason Mandatory reason / justification (min 5 chars)
   * @param paymentData Optional metadata (check number, bank, etc.)
   */
  substitutePayment(orderId: number, originalPaymentId: number, newMethod: number, reason: string, paymentData?: any): Observable<any> {
    const payload = {
      original_payment_id: originalPaymentId,
      new_method: newMethod,
      reason: reason,
      payment_data: paymentData,
    };
    return this.http.post<any>(`${environment.server_url}/sales/order/${orderId}/payment/substitute`, payload, {
      headers: jsonheader,
    });
  }

  /**
   * Queries available credit notes (`AVOIR`) that still have an unconsumed balance.
   * Calls `GET /sales/avoirs/available`.
   */
  getAvailableAvoirs(params: { customerId?: number; docRef?: string; limit?: number }): Observable<AvailableAvoirDto[]> {
    const queryParts: string[] = [];
    if (params.customerId != null && params.customerId > 0) {
      queryParts.push(`customer_id=${encodeURIComponent(params.customerId)}`);
    }
    if (params.docRef && params.docRef.trim().length > 0) {
      queryParts.push(`doc_ref=${encodeURIComponent(params.docRef.trim())}`);
    }
    if (params.limit != null) {
      queryParts.push(`limit=${encodeURIComponent(params.limit)}`);
    }
    const qs = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    return this.http.get<AvailableAvoirDto[]>(`${environment.server_url}/sales/avoirs/available${qs}`);
  }

  /**
   * Applies an available credit note (`AVOIR`) onto a target order in a single atomic transaction.
   * Calls `POST /sales/order/{id}/avoir/apply`.
   */
  applyAvoir(orderId: number, payload: ApplyAvoirPayload): Observable<any> {
    return this.http.post<any>(`${environment.server_url}/sales/order/${orderId}/avoir/apply`, payload, {
      headers: jsonheader,
    });
  }
}

export interface AvailableAvoirDto {
  avoir_order_id: number;
  customer_id: number;
  order_state: string;
  doc_ref: string;
  doc_link: string;
  customer_name: string;
  issue_date: string;
  receivable_id: number;
  receivable_state: string;
  initial_amount: number;
  consumed_amount: number;
  available_amount: number;
}

export interface ApplyAvoirPayload {
  avoir_order_id?: number;
  avoir_doc_ref?: string;
  amount?: number;
  comment?: string;
}

